import React, { useState, useEffect } from 'react';
import {
  mdiFilterOutline,
  mdiSortAlphabeticalAscending,
  mdiSortAlphabeticalDescending,
} from '@mdi/js';
import Icon from '@mdi/react';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Button,
  FormGroup,
  Label,
  Spinner,
  Col,
} from 'reactstrap';

import './FilterDropdown.scss';

function FilterDropdown({
  columnData,
  columnName,
  setColumnName,
  pageRequest,
  setPageRequest,
  handleManualSort,
  handleSetColumnName,
  isLoading = null,
  mapping = null,
  render = null,
}) {
  const [filterSearchTerm, setFilterSearchTerm] = useState('');
  const [ownFilter, setOwnFilter] = useState();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  useEffect(() => {
    if (!isDropdownOpen) {
      setOwnFilter({});
      setFilterSearchTerm('');
    }
  }, [isDropdownOpen]);

  const handleCheckbox = e => {
    e.stopPropagation();
    const { checked, name } = e.target;

    if (checked) {
      if (!ownFilter.key) {
        setOwnFilter({ key: columnName, values: [name] });
      } else {
        setOwnFilter({
          key: columnName,
          values: [...ownFilter.values, name],
        });
      }
    }
    if (!e.target.checked && ownFilter.key === columnName) {
      const newValues = ownFilter?.values?.filter(v => v !== name);
      setOwnFilter(
        newValues?.length ? { key: columnName, values: newValues } : {},
      );
    }
  };

  const handleApplyFilter = e => {
    e.stopPropagation();
    const updated = { ...pageRequest };
    if (!ownFilter?.key) {
      updated.filters = updated?.filters?.filter(
        item => item?.key !== columnName,
      );
    } else {
      const idx = updated?.filters?.findIndex(item => item?.key === columnName);
      if (idx !== -1) {
        updated.filters[idx] = ownFilter;
      } else {
        updated.filters.push(ownFilter);
      }
    }
    setColumnName('');
    setPageRequest(updated);
    setIsDropdownOpen(false);
  };

  const handleClearFilter = e => {
    e.stopPropagation();
    setOwnFilter({});
  };

  const checkIfSorted = direction =>
    direction === pageRequest?.sortDirection &&
    columnName === pageRequest?.sortColumn;

  const displayData = data => {
    if (!mapping) return data;
    if (mapping[columnName]) {
      return mapping[columnName][data] || data;
    }
    return data;
  };

  const renderData = data => {
    if (!render) return displayData(data);
    if (render[columnName]) {
      return render[columnName](data);
    }
    return displayData(data);
  };

  return (
    <div className="table-filter-dropdown">
      <Dropdown
        isOpen={isDropdownOpen}
        toggle={() => {
          setIsDropdownOpen(!isDropdownOpen);
        }}
        direction="down"
      >
        <DropdownToggle
          className="cursor-pointer"
          tag="div"
          onClick={e => {
            e.stopPropagation();
            setIsDropdownOpen(!isDropdownOpen);
            handleSetColumnName();
          }}
        >
          <Icon path={mdiFilterOutline} size={0.6} />
        </DropdownToggle>
        <DropdownMenu
          container="body"
          className="p-0 m-0 table-filter-dropdown-menu"
        >
          <DropdownItem
            tag="div"
            text
            header
            className="p-0 m-0 d-flex flex-column"
          >
            <div className="p-1 px-2">
              <small className="fw-semibold">SORT BY</small>
            </div>
            <div
              className={`p-1 px-2 cursor-pointer fs-6 table-filter-dropdown-filter-sort ${
                checkIfSorted('ASC') ? 'active' : ''
              }`}
              onClick={e => {
                e.stopPropagation();
                handleManualSort(columnName, 'ASC');
                setIsDropdownOpen(false);
              }}
            >
              <Icon
                path={mdiSortAlphabeticalAscending}
                size={0.6}
                className="table-filter-dropdown-sort-icon"
              />
              <span className="ms-1 small">Ascending</span>
            </div>
            <div
              className={`p-1 px-2 cursor-pointer fs-6 table-filter-dropdown-filter-sort ${
                checkIfSorted('DESC') ? 'active' : ''
              }`}
              onClick={e => {
                e.stopPropagation();
                handleManualSort(columnName, 'DESC');
                setIsDropdownOpen(false);
              }}
            >
              <Icon
                path={mdiSortAlphabeticalDescending}
                size={0.6}
                className="table-filter-dropdown-sort-icon"
              />
              <span className="ms-1 small">Descending</span>
            </div>
          </DropdownItem>
          <hr className="m-0 my-2" />
          <div className="px-2">
            <div className="position-relative">
              <Input
                className="external-filter-input py-1"
                placeholder="Search"
                type="text"
                value={filterSearchTerm}
                onChange={e => {
                  e.stopPropagation();
                  setFilterSearchTerm(e.target.value);
                }}
              />
            </div>
            <div className="table-filter-dropdown-filter-checkboxes">
              {isLoading && (
                <div className="filter-dropdown-spinner-container">
                  <Spinner size="sm" color="dark" />
                </div>
              )}
              {columnData
                ?.filter(
                  data =>
                    !filterSearchTerm ||
                    displayData(data)
                      ?.toLowerCase()
                      .includes(filterSearchTerm.toLowerCase()),
                )
                .map(data => (
                  <DropdownItem
                    tag="div"
                    key={data}
                    className="px-1 d-flex align-items-center gap-2"
                    onClick={e => e.stopPropagation()}
                    toggle={false}
                  >
                    <FormGroup
                      check
                      className="m-0 d-flex align-items-center gap-2"
                    >
                      <Input
                        id={data}
                        name={data}
                        className="form-check-input blue-checkbox my-0"
                        type="checkbox"
                        onChange={handleCheckbox}
                        onClick={e => e.stopPropagation()}
                        checked={
                          ownFilter?.key === columnName &&
                          ownFilter?.values.includes(data)
                        }
                      />
                      <Label
                        for={data}
                        className="small my-0"
                        style={{
                          maxWidth: '250px',
                          overflowX: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();

                          // Create a synthetic event that mimics the checkbox change event
                          const isCurrentlyChecked =
                            ownFilter?.key === columnName &&
                            ownFilter?.values.includes(data);
                          const syntheticEvent = {
                            target: {
                              checked: !isCurrentlyChecked,
                              name: data,
                            },
                            stopPropagation: () => {},
                          };

                          // Call your existing handleCheckbox function
                          handleCheckbox(syntheticEvent);
                        }}
                        title={renderData(data)}
                      >
                        {renderData(data)}
                      </Label>
                    </FormGroup>
                  </DropdownItem>
                ))}
            </div>
          </div>
          <div className="filter-actions d-flex flex-row align-items-center gap-1 mb-2">
            <Col>
              <Button
                outline
                onClick={e => {
                  handleClearFilter(e);
                  setIsDropdownOpen(false);
                }}
                className="custom-light-btn w-100 py-1"
              >
                Cancel
              </Button>
            </Col>
            <Col>
              <Button
                onClick={handleApplyFilter}
                className="custom-btn w-100 py-1"
                disabled={!ownFilter?.key && !ownFilter?.values?.length}
                style={{
                  backgroundColor: '#017EBD',
                  border: '#017EBD',
                }}
              >
                Apply
              </Button>
            </Col>
          </div>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}

export default FilterDropdown;
